import { Injectable, EventEmitter, ComponentFactory, ComponentRef } from '@angular/core';

import * as _ from 'lodash';
import { Subscription } from 'rxjs';

import { DrawerComponent } from './components/drawer/drawer.component';
import { DrawerHeaderComponent } from './components/partials/header/header.component';
import { DrawerFooterComponent } from './components/partials/footer/footer.component';

export class DrawerBase {
  constructor(
    private _drawers: Array<DrawerBase>,
    private _data: DrawerInjectData,
    public index: number
  ) {
    _drawers.push(this);
    this._updateParams();
  }

  public destroyed = false;
  public componentRef: ComponentRef<any>;
  public componentInstance: DrawerComponent;
  public headerInstances: Array<DrawerHeaderComponent> = [];
  public footerInstances: Array<DrawerFooterComponent> = [];

  public onShow: EventEmitter<any> = new EventEmitter();
  public onShown: EventEmitter<any> = new EventEmitter();

  public onHide: EventEmitter<any> = new EventEmitter();
  public onHidden: EventEmitter<any> = new EventEmitter();


  public onOpen: EventEmitter<any> = new EventEmitter();
  public onDismiss: EventEmitter<any> = new EventEmitter();
  public onClose: Promise<any> = new Promise((resolve) => this._onClosePromise = resolve);

  public onReturnValue: EventEmitter<any> = new EventEmitter();
  public onParamsChange: EventEmitter<DrawerParamsBase> = new EventEmitter();
  public onSettingsChange: EventEmitter<DrawerSettings> = new EventEmitter();

  public $onResizeRequested: EventEmitter<DrawerSettings> = new EventEmitter();
  public onResize: EventEmitter<DrawerSettings> = new EventEmitter();

  public $onHeaderListChange: EventEmitter<Array<DrawerHeaderComponent>> = new EventEmitter();
  public $onFooterListChange: EventEmitter<Array<DrawerFooterComponent>> = new EventEmitter();

  private _params: DrawerParamsBase = new DrawerParamsBase();
  private _onClosePromise: (value?: any) => void;

  public get visible(): boolean {
    return this.ready ? this.componentInstance.visible : false;
  }
  public set visible(visible: boolean) {
    if (this.ready) { this.componentInstance.visible = visible; }
  }

  public get ready(): boolean {
    return this.componentInstance ? this.componentInstance.ready : false;
  }
  public set ready(ready: boolean) {
    if (this.componentInstance) { this.componentInstance.ready = ready; }
  }

  public get component(): any { return this._data.component; }
  public get params(): DrawerParamsBase {
    return this._params;
  }
  public get settings(): DrawerSettings {
    let settings: DrawerSettings = this._data.settings;
    if (!settings) { settings = {}; }
    if (!(settings.backdrop && settings.backdrop.onClick)) {
      if (!settings.backdrop) { settings.backdrop = {}; }
      settings.backdrop.onClick = 'close';
    }
    if (!(settings.header && settings.header.closable && settings.header.closeClick)) {
      if (!settings.header) { settings.header = { title: null }; }
      if (settings.header.closable) { settings.header.closeClick = 'close'; }
    }
    return settings;
  }
  public get zindex(): number { return this._data.zindex; }
  public get content_zindex(): number { return this._data.content_zindex; }
  public get backdrop_zindex(): number { return this._data.backdrop_zindex; }

  public get close_on_route_change(): boolean {
    if (!this._data.settings) { this._data.settings = {}; }
    const close_on_route_change = this._data.settings.close_on_route_change;
    return _.isBoolean(close_on_route_change) ? close_on_route_change : true;
  }

  public setSize(size: DrawerSettingsSize) {
    if (!this._data.settings) { this._data.settings = {}; }
    this._data.settings.size = size;
    this.onSettingsChange.emit(this._data.settings);
  }

  public setBackdrop(backdrop: DrawerSettingsBackdrop) {
    if (!this._data.settings) { this._data.settings = {}; }
    this._data.settings.backdrop = backdrop;
    this.onSettingsChange.emit(this._data.settings);
  }

  public setKeyboard(keyboard: DrawerSettingsKeyboard) {
    if (!this._data.settings) { this._data.settings = {}; }
    this._data.settings.keyboard = keyboard;
    this.onSettingsChange.emit(this._data.settings);
  }

  public setHeader(header: DrawerSettingsHeader) {
    if (!this._data.settings) { this._data.settings = {}; }
    this._data.settings.header = header;
    this.onSettingsChange.emit(this._data.settings);
  }

  public setParams(params: DrawerParamsData) {
    this._data.params = params;
    this._updateParams();
    this.onParamsChange.emit(this._params);
  }

  public resize() {
    this.$onResizeRequested.emit();
  }

  public sizeClass(): string {
    switch (this.settings.size) {
      case 'xs': return 'drawer-xs';
      case 'sm': return 'drawer-sm';
      case 'md': return 'drawer-md';
      case 'lg': return 'drawer-lg';
      case 'responsive': return 'drawer-responsive';
      default: return 'drawer-md';
    }
  }

  public hasHeader(): boolean {
    return this.headerInstances.length > 0;
  }

  public hasFooter(): boolean {
    return this.footerInstances.length > 0;
  }

  public getOffsetHeight(): number {
    let height = 0;
    this.headerInstances.forEach((component) => height += component.elementRef.nativeElement.offsetHeight);
    this.footerInstances.forEach((component) => height += component.elementRef.nativeElement.offsetHeight);
    return height;
  }

  public addHeader(header: DrawerHeaderComponent): number {
    const index = this.headerInstances.length;
    this.headerInstances.push(header);
    this.$onHeaderListChange.emit(this.headerInstances);
    return index;
  }

  public removeHeader(index: number): boolean {
    if (this.headerInstances[index]) {
      this.headerInstances.splice(index, 1);
      this.$onHeaderListChange.emit(this.headerInstances);
      return true;
    }
    return false;
  }

  public addFooter(header: DrawerFooterComponent): number {
    const index = this.footerInstances.length;
    this.footerInstances.push(header);
    this.$onFooterListChange.emit(this.footerInstances);
    return index;
  }

  public removeFooter(index: number): boolean {
    if (this.footerInstances[index]) {
      this.footerInstances.splice(index, 1);
      this.$onFooterListChange.emit(this.footerInstances);
      return true;
    }
    return false;
  }

  public open() {
    if (this.destroyed) {
      console.warn('Is\'t possible to open a destroyed drawer.');
      return;
    }
    if (this.ready) {
      this.componentInstance.visible = true;
      this.onOpen.emit();
      this.onShow.emit();
    } else {
      setTimeout(() => this.open(), 100);
    }
  }

  public dismiss() {
    if (this.ready) {
      const hidden_subscription: Subscription = this.onHidden.subscribe(() => {
        hidden_subscription.unsubscribe();
        this.onDismiss.emit();
      });
      this.componentInstance.visible = false;
    } else {
      setTimeout(() => this.dismiss(), 100);
    }
  }

  public close() {
    if (this.ready) {
      const hidden_subscription: Subscription = this.onHidden.subscribe(() => {
        hidden_subscription.unsubscribe();
        this.onDismiss.emit();
        this._destroy();
      });
      this.componentInstance.visible = false;
    } else {
      setTimeout(() => this.close(), 100);
    }
  }

  public returnValue(response: any) {
    this.onReturnValue.emit(response);
  }

  private _updateParams() {
    this._params.params = this._data.params;
  }

  private _destroy() {
    let this_drawer_index: number;
    this._drawers.forEach((drawer, index) => {
      if (drawer.index == this.index) { this_drawer_index = index; }
    });
    if (this_drawer_index >= 0) {
      this._drawers.splice(this_drawer_index, 1);
      this._onClosePromise();
      this.destroyed = true;
    }
  }
}

@Injectable()
export class DrawerRefBase {
  public drawer: DrawerBase;
}

@Injectable()
export class DrawerParamsBase {
  public params: DrawerParamsData;
  public get(key: string) { return this.params ? this.params[key] : null; }
}

export type DrawerSettingsSize = 'xs' | 'sm' | 'md' | 'lg' | 'responsive';

export interface DrawerSettingsBackdrop {
  onClick?: 'close' | 'dismiss' | 'none';
}

export interface DrawerSettingsKeyboard {
  onEscape?: 'close' | 'dismiss' | 'none' | Function;
  onReturn?: 'close' | 'dismiss' | 'none' | Function;
}

export interface DrawerSettingsHeader {
  title: string;
  subtitle?: string;
  closable?: boolean;
  closeClick?: 'close' | 'dismiss';
}

export interface DrawerSettings {
  size?: DrawerSettingsSize;
  keyboard?: DrawerSettingsKeyboard;
  backdrop?: DrawerSettingsBackdrop;
  header?: DrawerSettingsHeader;
  close_on_route_change?: boolean;
}

export interface DrawerInjectData {
  component: ComponentFactory<any>;
  instance?: DrawerBase;
  params?: DrawerParamsData;
  settings?: DrawerSettings;
  zindex?: any;
  content_zindex?: any;
  backdrop_zindex?: any;
}

export interface DrawerParamsData {
  [key: string]: any;
}

export interface DrawerAlertParams {
  title?: string;
  subtitle?: string;
  message?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'responsive';
  buttonText?: string;
}

export interface DrawerConfirmParams {
  title?: string;
  subtitle?: string;
  message?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'responsive';
  resolveButtonText?: string;
  rejectButtonText?: string;
}

export type DrawerConfirmCallback = (response: boolean) => void;
